import { motion } from 'framer-motion';
import { ArrowRight, Eye } from 'lucide-react';

export const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#e62020]/5 via-transparent to-transparent" />
        
        {/* Animated grid - slower, more subtle */}
        <motion.div 
          className="absolute inset-0 cyber-grid opacity-[0.08]"
          animate={{ 
            backgroundPosition: ['0px 0px', '40px 40px']
          }}
          transition={{ 
            duration: 40, 
            repeat: Infinity, 
            ease: 'linear' 
          }}
        />
        
        {/* Fewer, slower particles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#e62020]/60 rounded-full"
            style={{
              left: `${15 + Math.random() * 70}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -150, 0],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 6 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: 'easeInOut',
            }}
          />
        ))}

        {/* Single subtle vortex ring */}
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2">
          <motion.div
            className="w-[500px] h-[500px] rounded-full border border-[#e62020]/[0.07]"
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      </div>

      {/* Content - LEFT ALIGNED */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="max-w-3xl">
          {/* Overline - offset slightly */}
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#e62020] text-xs tracking-[0.25em] uppercase mb-8 ml-1"
            data-testid="hero-caption"
          >
            San Antonio Web Design
          </motion.p>

          {/* Main Headline - left aligned, tighter */}
          <div className="mb-8">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-bold uppercase leading-[0.9] text-white mb-2"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              data-testid="hero-headline"
            >
              Your business
            </motion.h1>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-bold uppercase leading-[0.9] text-white mb-2"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              deserves to be
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex items-baseline gap-4"
            >
              <h1
                className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-bold uppercase leading-[0.9] text-[#e62020]"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                seen.
              </h1>
              {/* Hand-drawn style underline */}
              <motion.svg 
                viewBox="0 0 120 8" 
                className="w-24 h-2 -ml-2 mt-4"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.9 }}
              >
                <motion.path
                  d="M2 5 Q 30 2, 60 5 T 118 4"
                  fill="none"
                  stroke="#e62020"
                  strokeWidth="2"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                />
              </motion.svg>
            </motion.div>
          </div>

          {/* Subheadline - punchier copy */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="text-lg md:text-xl text-[#888888] max-w-xl mb-12 leading-relaxed"
            data-testid="hero-subheadline"
          >
            We build websites for businesses that don't have one yet.
            <span className="text-white"> Fast. Professional. Done right.</span>
          </motion.p>

          {/* CTA Buttons - staggered, not perfectly aligned */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="flex flex-col sm:flex-row items-start gap-4"
          >
            <motion.button
              onClick={() => scrollToSection('#contact')}
              className="group bg-[#e62020] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm hover:bg-[#c0392b] transition-colors"
              data-testid="hero-cta-primary"
              whileHover={{ scale: 1.02, x: 3 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="flex items-center gap-3">
                Get Started
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </motion.button>
            
            <motion.button
              onClick={() => scrollToSection('#gallery')}
              className="group text-[#888888] hover:text-white px-4 py-4 font-medium text-sm transition-colors flex items-center gap-2"
              data-testid="hero-cta-secondary"
              whileHover={{ x: 3 }}
            >
              <Eye size={16} />
              See our work
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator - offset to the right */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 right-12 hidden md:block"
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-2"
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#e62020]/50 to-transparent" />
          <span className="text-[10px] text-[#888888] tracking-widest uppercase rotate-90 origin-center translate-y-4">scroll</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
